import {
  ChatHistoryConversation,
  ChatMessageVO,
  DocumentRef,
  FileItem,
  QuestionAnswerPayload,
  QuestionData
} from '../services/aiAssistant';
import { ThinkingToolCall } from './ThinkingCard';
import { MessageTokens } from './TokenTag';

export interface ChatMsgThinking {
  text: string;
  tools: ThinkingToolCall[];
  status: 'in-progress' | 'done' | 'failed';
  expanded: boolean;
}

export interface ChatMsg {
  id: string;
  turnId: string;
  role: 'user' | 'ai';
  content: string;
  files?: FileItem[];
  documents?: DocumentRef[];
  thinking?: ChatMsgThinking;
  createTime?: string;
  tokens?: MessageTokens;
  /** Agent 抛出的结构化问题（askQuestion） */
  questionData?: QuestionData;
  /** 用户作答只读快照 */
  questionAnswers?: QuestionAnswerPayload;
}

const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

/** 从消息 content 中提取 askQuestion 问题数据（含 questionId 才算问题消息） */
export const extractQuestionData = (content: any): QuestionData | undefined => {
  if (content && typeof content === 'object' && content.questionId && Array.isArray(content.questions)) {
    return { questionId: content.questionId, questions: content.questions };
  }
  return undefined;
};

/** type=2 思考过程不渲染历史；含 questionId 的 type=2 保留为问题卡片；type=1/3 映射为本地 ChatMsg */
export const mapHistoryMessageToChatMsg = (m: ChatMessageVO, turnId: string): ChatMsg | null => {
  if (m.type === 2) {
    const questionData = extractQuestionData(m.content);
    if (!questionData) return null;
    return {
      id: m.id || uid(),
      turnId,
      role: 'ai',
      content: '',
      questionData,
      questionAnswers: (m.content as any)?.questionAnswers,
      createTime: m.createTime
    };
  }
  if (m.type === 1) {
    // type=1 USER_INPUT：content 形如 { agentId, sourceType, initiateType, message }
    const text = (m.content && ((m.content as any).message || (m.content as any).text)) || '';
    return { id: m.id || uid(), turnId, role: 'user', content: text, createTime: m.createTime };
  }
  if (m.type === 3) {
    const text = (m.content && (m.content as any).text) || '';
    return {
      id: m.id || uid(),
      turnId,
      role: 'ai',
      content: text,
      documents: m.attachedDocuments,
      createTime: m.createTime
    };
  }
  return null;
};

/** 扁平 ChatMessageVO[] → ChatMsg[]：所有产物共享同一 turnId（分享查看页等扁平场景复用） */
export const flattenMessages = (msgs: ChatMessageVO[], turnId: string): ChatMsg[] => {
  const result: ChatMsg[] = [];
  for (const m of msgs || []) {
    const mapped = mapHistoryMessageToChatMsg(m, turnId);
    if (!mapped) continue;
    result.push(mapped);
  }
  return result;
};

export const pickConvTokens = (conv: ChatHistoryConversation): MessageTokens | undefined => {
  const { inputTokenCnt, completeTokenCnt, cacheTokenCnt, tokenCount } = conv;
  const isMissing = (v: number | null | undefined) => v === null || v === undefined;
  if (isMissing(inputTokenCnt) && isMissing(completeTokenCnt) && isMissing(cacheTokenCnt) && isMissing(tokenCount)) {
    return undefined;
  }
  return {
    input: inputTokenCnt ?? undefined,
    complete: completeTokenCnt ?? undefined,
    cache: cacheTokenCnt ?? undefined,
    total: tokenCount ?? undefined
  };
};

/** 把按 uuid 分组的 conversations 展平为 ChatMsg[]：保持轮次顺序；失败轮次无 AGENT_RESULT 时补一条错误占位 AI 消息；每轮 token 写到该轮最后一条 ai 消息 */
export const flattenConversations = (conversations: ChatHistoryConversation[]): ChatMsg[] => {
  const result: ChatMsg[] = [];
  for (const conv of conversations || []) {
    const before = result.length;
    let hasAiResult = false;
    const convMsgs = flattenMessages(conv.messages || [], conv.uuid);
    for (const mapped of convMsgs) {
      if (mapped.role === 'ai') hasAiResult = true;
      result.push(mapped);
    }
    if (conv.status === 'FAILED' && result.length > before && !hasAiResult) {
      result.push({
        id: `ai_${conv.uuid}_failed`,
        turnId: conv.uuid,
        role: 'ai',
        content: '抱歉，请求发送失败，请稍后重试。',
        createTime: conv.finishTime || conv.createTime
      });
    }
    // 把本轮 tokens 挂到本轮最后一条 ai 消息上（含 FAILED 占位）
    const convTokens = pickConvTokens(conv);
    if (convTokens) {
      for (let i = result.length - 1; i >= before; i--) {
        if (result[i].role === 'ai') {
          result[i] = { ...result[i], tokens: convTokens };
          break;
        }
      }
    }
  }
  return result;
};
